import {
  useEffect,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { useAuth } from "./AuthProvider";

/**
 * Login screen — calls POST /v1/auth/login via the auth client. Branded shell
 * (SolarKey Advanced AI Solutions): the sign-in surface is part of the platform,
 * not a vendor screen. All colour/typography from the SolarKey tokens so it
 * follows light/dark like the rest of the app.
 */
/** Who resets a password by hand, until a sender identity exists and the
 *  self-service flow can actually email a reset link. */
const SUPPORT_EMAIL = "ajay@solar-key.com";

export function LoginScreen() {
  const { login, status, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const busy = status === "authenticating";
  const [focused, setFocused] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // A wrong password with Caps Lock on is the single most common "it won't let me
  // in" — worth saying before the attempt, not after.
  const [capsLock, setCapsLock] = useState(false);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void login({ email, password });
  }

  // Every route in the app currently shares one <title>, so a browser tab, a
  // bookmark and a history entry all read "SolarKey". Naming this one at least
  // makes the sign-in tab identifiable; the other 36 routes want the same treatment
  // from the shell, which is a separate pass.
  useEffect(() => {
    const previous = document.title;
    document.title = "Sign in · SolarKey";
    return () => {
      document.title = previous;
    };
  }, []);

  function trackCapsLock(e: ReactKeyboardEvent<HTMLInputElement>) {
    // getModifierState is unavailable on some synthetic events; ignore if absent.
    const on = e.getModifierState?.("CapsLock");
    if (typeof on === "boolean") setCapsLock(on);
  }

  const inputStyle = (name: string): CSSProperties => ({
    ...fieldStyle,
    borderColor: focused === name ? "var(--sk-color-accent)" : "var(--sk-color-border-2, var(--sk-color-border-1))",
    boxShadow: focused === name ? "0 0 0 3px var(--sk-color-accent-soft, rgba(37,99,235,.15))" : "none",
  });

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        {/* Brand lockup: mark + wordmark (Solar·ink / Key·accent) + rule + tagline */}
        <div style={brandRow}>
          <SolarKeyMark />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={wordmark}>
              <span style={{ color: "var(--sk-color-ink-1)" }}>Solar</span>
              <span style={{ color: "var(--sk-color-accent)" }}>Key</span>
            </span>
            <span style={brandRule} aria-hidden />
            <span style={brandTag}>Advanced AI Solutions</span>
          </div>
        </div>

        {/* Card */}
        <form onSubmit={onSubmit} aria-labelledby="login-title" style={cardStyle}>
          <h1 id="login-title" style={titleStyle}>Sign in</h1>
          <p style={subtitleStyle}>Enter your credentials to continue</p>

          {error ? (
            <p role="alert" style={errorStyle}>{error}</p>
          ) : null}

          <label style={labelBlock}>
            <span style={labelText}>Email</span>
            <input
              // `name` as well as autoComplete: password managers match on both, and
              // some only fill reliably when the field is named.
              name="email"
              type="email"
              autoComplete="username"
              // A phone would otherwise capitalise and spell-check an address.
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              maxLength={255}
              required
              // The first thing to do on this screen is type an address.
              autoFocus
              aria-invalid={error ? true : undefined}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocused("email")}
              onBlur={() => setFocused(null)}
              style={inputStyle("email")}
            />
          </label>

          {/* NOT a wrapping <label>: a <button> inside one is itself a labelable
              element, so the label bound to the reveal button instead of the input
              and a screen reader announced the wrong control. Explicit htmlFor/id
              keeps the field's accessible name exactly "Password". */}
          <div style={labelBlock}>
            <div style={labelRow}>
              <label htmlFor="login-password" style={labelText}>Password</label>
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={revealBtn}
                aria-pressed={showPassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <input
              id="login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              // bcrypt reads the first 72 bytes; a longer value cannot change the
              // outcome, so there is no reason to accept an unbounded one.
              maxLength={200}
              required
              aria-invalid={error ? true : undefined}
              aria-describedby={capsLock ? "login-caps" : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={trackCapsLock}
              onKeyDown={trackCapsLock}
              onFocus={() => setFocused("password")}
              onBlur={() => {
                setFocused(null);
                setCapsLock(false);
              }}
              style={inputStyle("password")}
            />
            {capsLock ? (
              <span id="login-caps" style={capsHint}>
                Caps Lock is on
              </span>
            ) : null}
          </div>

          <button type="submit" disabled={busy} style={{ ...buttonStyle, opacity: busy ? 0.65 : 1 }}>
            {busy ? "Signing in…" : "Sign in"}
          </button>

          {/* Until a sender identity exists there is no self-service reset, so this
              says who to ask rather than pretending to send a link. A dead
              "Forgot password?" that opens nothing would be worse than none. */}
          <p style={forgotRow}>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                "SolarKey platform — password reset request",
              )}`}
              style={forgotLink}
            >
              Forgot your password?
            </a>
          </p>
        </form>

        <p style={footerStyle}>SolarKey Advanced AI Solutions · Powered by AI</p>
      </div>
    </div>
  );
}

/**
 * SolarKey brand mark — the ring-of-arcs + key stem (key through a sun/clock).
 * Same geometry as the production /public/solarkey-logo.svg, inlined so the
 * "black" arcs follow the ink token and stay visible in dark mode (the blue arc
 * keeps the brand accent).
 */
function SolarKeyMark() {
  return (
    <svg width="42" height="42" viewBox="0 0 100 100" fill="none" role="img" aria-label="SolarKey" style={{ flexShrink: 0 }}>
      <path d="M 56.3 14.5 A 36 36 0 0 1 86 50" stroke="var(--sk-color-ink-1)" strokeWidth="5.5" strokeLinecap="round" />
      <path d="M 86 50 A 36 36 0 0 1 14 50" stroke="var(--sk-color-accent)" strokeWidth="5.5" strokeLinecap="round" />
      <path d="M 14 50 A 36 36 0 0 1 43.7 14.5" stroke="var(--sk-color-ink-1)" strokeWidth="5.5" strokeLinecap="round" />
      <path d="M 50 7 L 50 53 L 58 53" stroke="var(--sk-color-ink-1)" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Styles (SolarKey tokens; theme-aware via the token flip) ─────────────── */
const pageStyle: CSSProperties = {
  // 100dvh, not 100vh: on a phone browser 100vh counts the area behind the address
  // bar, so the card sat partly off-screen and the page scrolled for no reason.
  // `100vh` stays as the fallback for anything without dvh support.
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "var(--sk-color-surface-1)",
  color: "var(--sk-color-ink-1)",
  fontFamily: "var(--sk-font-text)",
};
const shellStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 20,
  width: "100%",
  maxWidth: 380,
};
const brandRow: CSSProperties = { display: "flex", alignItems: "center", gap: 12 };
const wordmark: CSSProperties = {
  font: "800 26px/1 var(--sk-font-text)",
  letterSpacing: "-0.02em",
};
const brandRule: CSSProperties = {
  display: "block",
  height: 2,
  margin: "5px 0 5px",
  borderRadius: 1,
  background: "var(--sk-color-accent)",
};
const brandTag: CSSProperties = {
  font: "600 10px/1.2 var(--sk-font-text)",
  letterSpacing: "0.3em",
  textTransform: "uppercase",
  color: "var(--sk-color-ink-3)",
};
const cardStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  padding: 28,
  borderRadius: "var(--sk-radius-card, 12px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-color-surface-0)",
  boxShadow: "var(--sk-shadow-card, 0 6px 24px rgba(10,10,15,.08))",
};
const titleStyle: CSSProperties = {
  margin: 0,
  font: "700 20px/1.2 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const subtitleStyle: CSSProperties = {
  margin: "4px 0 20px",
  font: "400 13px/1.4 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};
const errorStyle: CSSProperties = {
  margin: "0 0 14px",
  padding: "8px 12px",
  borderRadius: 8,
  background: "color-mix(in srgb, var(--sk-color-status-fault, #ef4444) 10%, transparent)",
  color: "var(--sk-color-status-fault, #dc2626)",
  font: "500 13px/1.4 var(--sk-font-text)",
};
const labelBlock: CSSProperties = { display: "block", marginBottom: 14 };
const labelRow: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
};
const revealBtn: CSSProperties = {
  marginBottom: 6,
  padding: 0,
  border: 0,
  background: "transparent",
  color: "var(--sk-color-accent)",
  font: "600 12px/1.3 var(--sk-font-text)",
  cursor: "pointer",
};
const capsHint: CSSProperties = {
  display: "block",
  marginTop: 6,
  font: "500 12px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-status-warning, #b45309)",
};
const forgotRow: CSSProperties = {
  margin: "16px 0 0",
  textAlign: "center",
};
const forgotLink: CSSProperties = {
  color: "var(--sk-color-accent)",
  font: "600 13px/1.3 var(--sk-font-text)",
  textDecoration: "none",
};
const labelText: CSSProperties = {
  display: "block",
  marginBottom: 6,
  font: "600 13px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-ink-2, var(--sk-color-ink-1))",
};
const fieldStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  height: 42,
  padding: "0 12px",
  borderRadius: "var(--sk-radius-control, 8px)",
  border: "1px solid var(--sk-color-border-2, var(--sk-color-border-1))",
  background: "var(--sk-color-surface-1)",
  color: "var(--sk-color-ink-1)",
  font: "400 14px/1.4 var(--sk-font-text)",
  outline: "none",
  transition: "border-color .12s, box-shadow .12s",
};
const buttonStyle: CSSProperties = {
  width: "100%",
  height: 44,
  marginTop: 6,
  border: 0,
  borderRadius: "var(--sk-radius-control, 8px)",
  background: "var(--sk-color-accent)",
  color: "#fff",
  font: "600 15px/1 var(--sk-font-text)",
  cursor: "pointer",
};
const footerStyle: CSSProperties = {
  margin: 0,
  font: "400 12px/1.4 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
  textAlign: "center",
};
